import * as THREE from 'three';
import { EventDispatcher } from '../../EventDispatcher.js';
import { setMaxNodesLoading, Utils } from '../../Potree.js';
import { getCurrentIdToken } from '../../utils/CurrentToken.js';

const panoramaConfigurationValue = {
    Arrow: 'arrow',
    Bubble: 'bubble',
    Navigation: 'navigation',
    GroundCircles: 'groundCircles',
};

const defaultPanoramaGroundCirclesColor = 0xffffff;
const maxImagesToPreload = 20;
const sphereOpactiy = 0.6;
const cameraModePerspective = 1;
const minTweenZoom = 1.4;
const maxTweenZoom = 2.8;
const maxCachedImages = 100;
const imageCacheEvictCount = 40;

class Image360 {
    constructor(file, time, longitude, latitude, altitude, course, pitch, roll, real, thumbnailPath = null) {
        this.file = file;
        this.thumbnailPath = thumbnailPath;
        this.time = time;
        this.longitude = longitude;
        this.latitude = latitude;
        this.altitude = altitude;
        // this.course = course;
        // this.pitch = pitch;
        // this.roll = roll;
        this.real = real;
        this.isFocusingImage = false;

        function toAngle(x) {
            return (x * 180) / Math.PI;
        }

        function toReal(x) {
            if (!isNaN(parseFloat(x)) && isFinite(parseFloat(x))) {
                return parseFloat(parseFloat(x).toFixed(7));
            } else {
                return x;
            }
        }

        if (real) {
            //var quat = new THREE.Quaternion(roll, pitch, course, real);
            var quat = new THREE.Quaternion(course, pitch, roll, real);

            var q = quat;
            var m = new THREE.Matrix4();
            m.makeRotationFromQuaternion(q);

            var axis = [0, 0, 0];
            var angle = 2 * Math.acos(q.w);
            if (1 - q.w * q.w < 0.000001) {
                axis[0] = q.x;
                axis[1] = q.y;
                axis[2] = q.z;
            } else {
                // http://www.euclideanspace.com/maths/geometry/rotations/conversions/quaternionToAngle/
                var s = Math.sqrt(1 - q.w * q.w);
                axis[0] = q.x / s;
                axis[1] = q.y / s;
                axis[2] = q.z / s;
            }

            var eu = new THREE.Euler();
            eu.setFromRotationMatrix(m, 'XYZ');

            this.roll = toReal(toAngle(eu.toArray()[0]));
            this.pitch = toReal(toAngle(eu.toArray()[1]));
            this.course = 90 - toReal(toAngle(eu.toArray()[2]));
        } else {
            this.course = course;
            this.pitch = pitch;
            this.roll = roll;
        }

        this.mesh = null;
    }
}

export class Images360 extends EventDispatcher {
    constructor(viewer, facilitySphereRadius, groundCirclesColor, potreeId, splitScreenViewer) {
        super();

        this.viewer = viewer;
        this.splitScreenViewer = splitScreenViewer;
        this.hideNavigationArrow = false;
        this.imageCache = [];
        this.cancelPromises = false;
        this.facilitySphereRadius = facilitySphereRadius || 4;
        this.currentlyHovered = null;

        this.focusByClick = false;
        this.imageUnfocused = false;
        this.panoramaImageSetting = window.localStorage.getItem('panoramaImageSetting') || panoramaConfigurationValue.Bubble;
        this.panoramaGroundCirclesColor = groundCirclesColor || defaultPanoramaGroundCirclesColor;
        this.lastPanoramaGroundCirclesColor = this.panoramaGroundCirclesColor;

        this.lastPanoramaImageSetting = this.panoramaImageSetting;
        this.potreeId = potreeId;
        this.potreeElement = document.getElementById(potreeId);
        this.loadingIndicator = document.getElementById(potreeId + 'panorama_loader');

        this.selectingEnabled = true;
        this.splitScreen = false;
        this.panoramicFactor = null;

        // Track crop tool volume visibility for panorama mode
        this.cropToolVolumesVisibilityState = new Map();

        this.images = [];
        this.node = {};

        this.sg = new THREE.SphereGeometry(0.3, 16, 16);
        this.sgHigh = new THREE.SphereGeometry(1, 128, 128);

        this.sm = new THREE.MeshStandardMaterial({
            side: THREE.BackSide,
            color: 0xffffff,
            transparent: true,
            opacity: sphereOpactiy,
            emissive: 0x88badd,
            roughness: 0.287,
            metalness: 0.385,
        });
        this.smHovered = new THREE.MeshStandardMaterial({
            side: THREE.BackSide,
            color: 0x0000ff,
            transparent: true,
            opacity: 0.2,
            roughness: 0.287,
            metalness: 0.385,
        });
        this.smLoader = new THREE.MeshBasicMaterial({
            side: THREE.BackSide,
        });
        this.circleGeometry = new THREE.CircleGeometry(0.15, 32);
        this.circleMaterial = new THREE.MeshBasicMaterial({
            color: this.panoramaGroundCirclesColor,
            side: THREE.DoubleSide,
            transparent: true,
            opacity: sphereOpactiy,
        });
        this.circleMaterialHovered = new THREE.MeshBasicMaterial({
            color: 0x0000ff,
            side: THREE.DoubleSide,
            transparent: true,
            opacity: 0.2,
        });
        this.textureLoader = new THREE.TextureLoader();

        this.raycaster = new THREE.Raycaster();

        this.sphere = new THREE.Mesh(this.sgHigh, this.smLoader);
        this.sphere.name = 'mainPanoramicSphere';
        // Used so the mainPanoramicSphere is not affected by the crop tool
        this.sphere.material.onBeforeCompile = (shader) => {
            shader.fragmentShader = shader.fragmentShader.replace(
                '#include <clipping_planes_fragment>',
                '// Clipping removed for panoramic sphere'
            );
        };

        this.sphere.visible = false;
        this.sphere.scale.set(1000, 1000, 1000);

        this.navigationArrowImages = this.getNavigationArrowImages();
        this.navigationArrow = {};
        this.navigationArrowTextureCache = new Map();
        // this.updateNavigationArrowImage('Straight', true);
        // this.node.add(this.sphere);
        this._visible = true;
        // this.node.add(label);
        this.focusedImage = null;

        this.domRoot = this.viewer.renderer.domElement.parentElement;
        this.elUnfocus = this.createUnfocusButton();
        this.domRoot.appendChild(this.elUnfocus);

        this.elSplitScreen = this.createSplitScreenButton();
        this.domRoot.appendChild(this.elSplitScreen);

        this.elWallpaperOutlined = this.createWallpapperButton();
        this.domRoot.appendChild(this.elWallpaperOutlined);

        this.navigationArrowElement =
            this.panoramaImageSetting === panoramaConfigurationValue.GroundCircles
                ? this.createGroundCricleMousePointer()
                : this.createNavigationArrowButton();
        this.domRoot.appendChild(this.navigationArrowElement);

        this.viewer.addEventListener('update', () => {
            this.update(viewer);
        });
        this.viewer.inputHandler.addInputListener(this);

        this.viewer.isDragging = false;

        this.viewer.previousView = {
            controls: null,
            position: null,
            target: null,
            moveSpeed: null,
        };

        let startX = 0;
        let startY = 0;

        const handleStart = (event) => {
            this.viewer.isDragging = false;
            startX = event.mouse ? event.mouse.x : event.changedTouches ? event.changedTouches[0].clientX : 0;
            startY = event.mouse ? event.mouse.y : event.changedTouches ? event.changedTouches[0].clientY : 0;
        };

        const handleMove = (event) => {
            if (!this.viewer.isDragging) {
                const threshold = 5;
                const clientX = event.clientX ? event.clientX : event.changedTouches ? event.changedTouches[0].clientX : 0;
                const clientY = event.clientY ? event.clientY : event.changedTouches ? event.changedTouches[0].clientY : 0;
                if (clientX && clientY) {
                    const deltaX = Math.abs(clientX - startX);
                    const deltaY = Math.abs(clientY - startY);
                    if (deltaX > threshold || deltaY > threshold) {
                        this.viewer.isDragging = true;
                    }
                }
            }
        };

        const navigateToClosestImage = () => {
            const mouse = this.viewer.inputHandler.mouse;
            const camera = this.viewer.scene.getActiveCamera();
            const domElement = this.viewer.renderer.domElement;

            const ray = Potree.Utils.mouseToRay(mouse, camera, domElement.clientWidth, domElement.clientHeight);
            this.raycaster.ray.copy(ray);

            const allChildren = Object.values(this.node).flatMap((object3d) => object3d.children);
            const visibleChildren = allChildren.filter((child) => this.isChildNodeVisible(child, this.focusedImage));
            const intersections = this.raycaster.intersectObjects(visibleChildren);
            let closestImage = null;

            if (intersections.length > 0) {
                const closestIntersection = intersections.reduce(
                    (closest, intersection) => (intersection.distance < closest.distance ? intersection : closest),
                    intersections[0]
                );

                closestImage = closestIntersection.object.image360;
            } else {
                const worldPos = new THREE.Vector3();
                closestImage = visibleChildren.reduce(
                    (closest, child) => {
                        child.getWorldPosition(worldPos);
                        const distance = ray.distanceToPoint(worldPos);
                        return distance < closest.distance ? { image360: child.image360, distance } : closest;
                    },
                    { image360: null, distance: Infinity }
                ).image360;
            }
            if (closestImage) {
                this.resetToOriginalMesh(closestImage);
                this.focus(closestImage);
            }
        };

        const handleEnd = () => {
            // Prevent image navigation/focus changes while inserting an annotation
            if (
                this.viewer &&
                this.viewer.annotationTool &&
                typeof this.viewer.annotationTool.isInsertionActive === 'function' &&
                this.viewer.annotationTool.isInsertionActive()
            ) {
                this.viewer.isDragging = false;
                return;
            }

            if (!this.viewer.isDragging && this.focusedImage !== null && this.currentlyHovered && this.currentlyHovered.image360) {
                this.resetToOriginalMesh(this.currentlyHovered.image360);
            }
            if (
                !this.viewer.isDragging &&
                (this.panoramaImageSetting === panoramaConfigurationValue.Navigation ||
                    this.panoramaImageSetting === panoramaConfigurationValue.GroundCircles) &&
                this.focusedImage !== null
            ) {
                navigateToClosestImage();
            } else if (!this.viewer.isDragging && this.currentlyHovered && this.currentlyHovered.image360) {
                this.focusByClick = true;
                if (this.currentlyHovered.image360.focus) {
                    this.currentlyHovered.image360.focus();
                }
                this.focus(this.currentlyHovered.image360);
            }
            this.viewer.isDragging = false;
        };

        this.addEventListener('mousedown', handleStart);
        document.addEventListener('mousemove', handleMove);
        this.addEventListener('mouseup', handleEnd);

        this.addEventListener('touchstart', handleStart);
        document.addEventListener('touchmove', handleMove);
        this.addEventListener('touchend', handleEnd);
    }

    createNode(key) {
        this.node[key] = new THREE.Object3D();
        this.viewer.scene.scene.add(this.sphere);
        // this.node[key].add(this.sphere);
    }

    createUnfocusButton() {
        let elUnfocus = document.createElement('input');
        elUnfocus.type = 'image';
        elUnfocus.title = 'Exit 360 mode';
        elUnfocus.value = 'unfocus';
        elUnfocus.id = 'unfocus';
        elUnfocus.src = Potree.resourcePath + '/icons/remove.svg';
        elUnfocus.style.position = 'absolute';
        const potreeRenderArea = document.getElementById(this.potreeId);
        if (potreeRenderArea) {
            elUnfocus.style.left = '50%';
            elUnfocus.style.transform = 'translateX(-50%)';
        }
        elUnfocus.style.bottom = '10px';
        elUnfocus.style.zIndex = '10000';
        elUnfocus.style.fontSize = '2em';
        elUnfocus.style.background = 'rgb(238, 238, 238, 0.8)';
        elUnfocus.style.height = '36px';
        elUnfocus.style.width = '36px';
        elUnfocus.style.borderRadius = '5px';
        elUnfocus.addEventListener('click', (e) => {
            this.unfocus(e.detail.usedViewButtons);
        });

        elUnfocus.style.display = 'none';
        elUnfocus.setAttribute('data-html2canvas-ignore', '');
        return elUnfocus;
    }

    createSplitScreenButton() {
        let elSplitScreen = document.createElement('input');
        elSplitScreen.type = 'image';
        elSplitScreen.title = 'Split screen';
        elSplitScreen.value = 'splitScreen';
        elSplitScreen.id = 'splitScreen';
        elSplitScreen.src = Potree.resourcePath + '/icons/split_screen.svg';
        elSplitScreen.style.position = 'absolute';
        const potreeRenderArea = document.getElementById(this.potreeId);
        if (potreeRenderArea) {
            elSplitScreen.style.right = '50%';
            elSplitScreen.style.transform = 'translateX(-50%)';
            elSplitScreen.style.marginLeft = '36px';
            elSplitScreen.style.marginRight = '4px';
        }
        elSplitScreen.style.bottom = '10px';
        elSplitScreen.style.zIndex = '10000';
        elSplitScreen.style.fontSize = '2em';
        elSplitScreen.style.height = '36px';
        elSplitScreen.style.width = '36px';
        elSplitScreen.style.background = 'rgb(238, 238, 238, 0.8)';
        elSplitScreen.style.borderRadius = '5px';
        elSplitScreen.style.display = 'none';
        elSplitScreen.setAttribute('data-html2canvas-ignore', '');
        return elSplitScreen;
    }

    createWallpapperButton() {
        let elWallpaperOutlined = document.createElement('input');
        elWallpaperOutlined.type = 'image';
        elWallpaperOutlined.title = 'Set default image';
        elWallpaperOutlined.value = 'WallpaperOutlined';
        elWallpaperOutlined.id = 'WallpaperOutlined';
        elWallpaperOutlined.src = Potree.resourcePath + '/icons/wallpaper_outlined.svg';
        elWallpaperOutlined.style.position = 'absolute';
        const potreeRenderArea = document.getElementById(this.potreeId);
        if (potreeRenderArea) {
            elWallpaperOutlined.style.left = '50%';
            elWallpaperOutlined.style.transform = 'translateX(-50%)';
            elWallpaperOutlined.style.marginLeft = '40px';
        }
        elWallpaperOutlined.style.bottom = '10px';
        elWallpaperOutlined.style.zIndex = '10000';
        elWallpaperOutlined.style.fontSize = '2em';
        elWallpaperOutlined.style.height = '36px';
        elWallpaperOutlined.style.width = '36px';
        elWallpaperOutlined.style.background = 'rgb(238, 238, 238, 0.8)';
        elWallpaperOutlined.style.borderRadius = '5px';
        elWallpaperOutlined.style.display = 'none';
        elWallpaperOutlined.setAttribute('data-html2canvas-ignore', '');
        return elWallpaperOutlined;
    }

    createNavigationArrowButton() {
        let navigationArrowElement = document.createElement('input');
        navigationArrowElement.type = 'image';
        navigationArrowElement.title = 'Navigation Arrow';
        navigationArrowElement.value = 'navigationArrow';
        navigationArrowElement.id = 'navigationArrow';
        navigationArrowElement.src = Potree.resourcePath + '/icons/navigationArrows/panaromic-arrow-top.png';
        navigationArrowElement.style.position = 'absolute';
        navigationArrowElement.style.zIndex = '9000';
        navigationArrowElement.style.background = 'transparent';
        navigationArrowElement.style.height = '50px';
        navigationArrowElement.style.width = '50px';
        navigationArrowElement.style.display = 'none';
        navigationArrowElement.style.pointerEvents = 'none';

        return navigationArrowElement;
    }

    createGroundCricleMousePointer() {
        let navigationArrowElement = document.createElement('div');
        navigationArrowElement.id = 'navigationArrow';
        navigationArrowElement.title = 'Navigation Arrow';
        navigationArrowElement.style.position = 'absolute';
        navigationArrowElement.style.zIndex = '9000';
        navigationArrowElement.style.background = 'rgba(238, 238, 238, 0.9)';
        navigationArrowElement.style.borderRadius = '50%';
        navigationArrowElement.style.height = '24px';
        navigationArrowElement.style.width = '24px';
        navigationArrowElement.style.display = 'none';
        navigationArrowElement.style.pointerEvents = 'none';
        navigationArrowElement.style.boxShadow = '0 1px 4px rgba(0,0,0,0.08)';
        navigationArrowElement.style.border = '1px solid #ccc';

        return navigationArrowElement;
    }

    getNavigationArrowImages() {
        return {
            Straight: Potree.resourcePath + '/icons/navigationArrows/panaromic-arrow-top.png',
            RightTilt45: Potree.resourcePath + '/icons/navigationArrows/panaromic-arrow-top-right-45.png',
            LeftTilt45: Potree.resourcePath + '/icons/navigationArrows/panaromic-arrow-top-left-45.png',
            RightTilt22: Potree.resourcePath + '/icons/navigationArrows/panaromic-arrow-top-right-22.png',
            LeftTilt22: Potree.resourcePath + '/icons/navigationArrows/panaromic-arrow-top-left-22.png',
        };
    }

    set visible(visible) {
        if (this._visible === visible) {
            return;
        }

        for (const image of this.images) {
            image.mesh.visible = visible && this.focusedImage == null;
        }

        this.sphere.visible = visible && this.focusedImage != null;
        this._visible = visible;
        this.dispatchEvent({
            type: 'visibility_changed',
            images: this,
        });
    }

    get visible() {
        return this._visible;
    }

    updateSphere(image360) {
        let target = new THREE.Vector3();
        image360.mesh.getWorldPosition(target);
        this.sphere.position.copy(target);
        this.sphere.visible = !this.splitScreen;

        this.sphere.material = this.smLoader;
        this.sphere.material.map = image360.texture;
        this.sphere.material.needsUpdate = true;
        let { course, pitch, roll } = image360;
        let imageRotation = new THREE.Euler(
            THREE.MathUtils.degToRad(+roll + 90),
            THREE.MathUtils.degToRad(-pitch),
            THREE.MathUtils.degToRad(-course + 90),
            'ZYX'
        );
        imageRotation.x += image360.parentNode.rotation.x;
        imageRotation.y += image360.parentNode.rotation.y;
        imageRotation.z += image360.parentNode.rotation.z;
        this.sphere.rotation.copy(imageRotation);
    }

    moveSpherePosition(visibleImages) {
        this.viewer.scene.cameraP.zoom = 1;
        this.updateNavigationArrowDisplay();

        if (visibleImages && visibleImages.length) {
            visibleImages.sort((a, b) => a.distance - b.distance);
            this.fetchNearestImages(visibleImages, 0);
        }
    }

    updateNavigationArrowDisplay() {
        if (
            this.panoramaImageSetting === panoramaConfigurationValue.Navigation ||
            this.panoramaImageSetting === panoramaConfigurationValue.GroundCircles
        ) {
            this.navigationArrowElement.style.display = 'block';
        } else if (this.panoramaImageSetting === panoramaConfigurationValue.Arrow) {
            this.navigationArrow.visible = true;
        }
    }

    fetchNearestImages(visibleImages) {
        const imagesToLoad = visibleImages.slice(0, maxImagesToPreload);

        Promise.all(imagesToLoad.map(({ image }) => this.load(image, true))).then(() => {});
    }

    focus(image360, cameraTarget) {
        if (this.isFocusingImage) return;
        this.isFocusingImage = true;
        if (this.viewer.hidePointcloudsInPanorama) {
            Utils.handlePointCloudTransparencySliderChange(1, this.viewer.useHQ);
        }

        this.handleCancelCalibratedMeasurements();
        this.cancelActiveMeasuring();
        this.focusByClick = true;
        setMaxNodesLoading(0);
        if (!this.images.find((x) => x.file === image360.file)) return;
        this.cancelPromises = true;

        window.dispatchEvent(
            new CustomEvent('panoramaFocused', {
                detail: { image360 },
            })
        );

        this.elUnfocus.style.display = '';

        this.imageUnfocused = false;
        if (typeof image360.parentNode.originalRotation === 'undefined') {
            image360.parentNode.originalRotation = new THREE.Euler().copy(image360.parentNode.rotation);
        }
        this.viewer.previousView = {
            controls: this.viewer.previousView.controls ? this.viewer.previousView.controls : this.viewer.controls,
            position: this.viewer.previousView.position ? this.viewer.previousView.position : this.viewer.scene.view.position.clone(),
            target: this.viewer.previousView.target ? this.viewer.previousView.target : this.viewer.scene.view.getPivot(),
            moveSpeed: this.viewer.previousView.moveSpeed ? this.viewer.previousView.moveSpeed : this.viewer.getMoveSpeed(),
        };

        this.viewer.setControls(this.viewer.orbitControls);
        this.viewer.orbitControls.doubleClockZoomEnabled = false;
        this.viewer.orbitControls.invertYaw = true;
        this.viewer.orbitControls.invertPitch = true;
        this.viewer.orbitControls.rotationSpeed = 2;
        this.viewer.orbitControls.removeEventListeners('mousewheel');
        this.viewer.orbitControls.addEventListener('mousewheel', (event) => {
            if (this.viewer.scene.cameraP.zoom === 1 && event.delta < 0) return;
            this.viewer.scene.cameraP.zoom += event.delta * 0.25;
        });

        if (this.focusedImage) {
            this.navigationArrow.visible = false;
            this.navigationArrowElement.style.display = 'none';
        }
        this.loadingIndicator.style.display = 'block';

        this.selectingEnabled = true;

        this.load(image360, false).then((texture) => {
            this.loadingIndicator.style.display = 'none';
            if (this.imageUnfocused || !texture) {
                this.isFocusingImage = false;
                return;
            }

            let target = new THREE.Vector3();
            image360.mesh.getWorldPosition(target);
            let referencePoint = cameraTarget ? cameraTarget : this.viewer.scene.view.position;
            let dir = target.clone().sub(referencePoint).normalize();
            let move = dir.multiplyScalar(0.000001);
            let newCamPos = target.clone().sub(move);
            this.viewer.scene.view.setView(newCamPos, target, 500);

            if (!this.focusedImage) {
                this.hideCropToolVolumes();
                this.updateNavigationArrowDisplay();
                this.imageTextureLoaded(image360, cameraTarget, texture);
                return null;
            }
            const distance = cameraTarget ? cameraTarget.distanceTo(target) : this.viewer.scene.view.position.distanceTo(target);

            const startZoom = { zoom: 1 };
            const targetZoom = { zoom: this.getZoomFromDistance(distance) };

            this.zoomTween = new TWEEN.Tween(startZoom)
                .to(targetZoom, 800)
                .easing(TWEEN.Easing.Quadratic.Out)
                .onUpdate(() => {
                    image360.mesh.material.opacity = 0;
                    if (this.imageUnfocused && this.zoomTween) {
                        this.zoomTween.stop();
                        this.zoomTween = null;
                        return;
                    }
                    this.viewer.scene.cameraP.zoom = startZoom.zoom;
                    this.navigationArrow.visible = false;
                    this.navigationArrowElement.style.display = 'none';
                })
                .onComplete(() => {
                    this.isFocusingImage = false;
                    this.zoomTween = null;
                    this.hideCropToolVolumes();
                    this.updateNavigationArrowDisplay();
                    this.imageTextureLoaded(image360, cameraTarget, texture);
                })
                .onStop(() => {
                    this.isFocusingImage = false;
                    image360.mesh.material.opacity = sphereOpactiy;
                    this.viewer.scene.cameraP.zoom = 1;
                    this.focusedImage = null;
                })
                .start();
        });
        if (!this.focusedImage) {
            this.isFocusingImage = false;
            this.moveSpherePosition();
        }
    }

    getZoomFromDistance(distance) {
        let t = (distance - maxTweenZoom) / (10 - maxTweenZoom);
        t = Math.max(0, Math.min(1, t));
        t = Math.pow(t, 2);

        return minTweenZoom + t * (maxTweenZoom - minTweenZoom);
    }

    imageTextureLoaded(image360, cameraTarget, texture) {
        if (this.imageUnfocused) return;
        if (!this.splitScreen) {
            this.sphere.visible = true;
        } else {
            this.sphere.visible = false;
        }
        this.updateSphere(image360);
        const visibleImages = [];

        for (let image of this.images) {
            // ignore images that are not in the same storey
            if (!(image.position[2] >= image360.position[2] - 2 && image.position[2] <= image360.position[2] + 2)) {
                image.mesh.visible = false;
                image.mesh.showImage = false;
                continue;
            }
            image.mesh.visible = false;

            this.distance = Math.sqrt(
                Math.pow(image360.position[2] - image.position[2], 2) +
                    Math.pow(image360.position[1] - image.position[1], 2) +
                    Math.pow(image360.position[0] - image.position[0], 2)
            );
            // ignore images that are very close
            if (this.distance > 2 && this.distance < this.facilitySphereRadius) {
                if (
                    this.panoramaImageSetting === panoramaConfigurationValue.Arrow ||
                    this.panoramaImageSetting === panoramaConfigurationValue.Navigation
                ) {
                    this.handleArrowAndNavigationSetting(image);
                } else if (this.panoramaImageSetting === panoramaConfigurationValue.GroundCircles) {
                    this.handleGroundCirclesSetting(image);
                } else {
                    this.handleBubbleSetting(image);
                }
                if (!this.imageCache[image.file]) {
                    visibleImages.push({ image: image, distance: this.distance });
                }
            } else {
                image.mesh.showImage = false;
            }
        }
        this.cancelPromises = false;

        this.moveSpherePosition(visibleImages);
        this.focusedImage = image360;
        this.elSplitScreen.style.display = '';
        this.elWallpaperOutlined.style.display = '';
        this.dispatchEvent({
            type: 'image_focused',
        });
        setMaxNodesLoading(4);
    }

    load(image360, isPreLoad) {
        return new Promise((resolve, reject) => {
            if (!isPreLoad && Object.keys(this.imageCache).length >= maxCachedImages) {
                const keys = Object.keys(this.imageCache);
                const keysToRemove = keys.slice(0, imageCacheEvictCount);
                keysToRemove.forEach((key) => {
                    if (this.imageCache[key] && typeof this.imageCache[key].dispose === 'function') {
                        this.imageCache[key].dispose();
                    }
                    delete this.imageCache[key];
                });
            }

            if (this.imageCache[image360.file] && !image360.imageRecognitionFile) {
                if (!isPreLoad) image360.texture = this.imageCache[image360.file];
                resolve(this.imageCache[image360.file]);
                return;
            }

            const fileUrl = image360.imageRecognitionFile || image360.file;
            const token = getCurrentIdToken();
            fetch(fileUrl, {
                headers: { Authorization: `Bearer ${token}` },
            })
                .then((response) => response.json())
                .then((responseData) => {
                    let texture = this.textureLoader.load(responseData, (texture) => {
                        texture.colorSpace = THREE.SRGBColorSpace;
                        texture.generateMipmaps = true;
                        texture.minFilter = THREE.LinearMipmapLinearFilter;

                        texture.wrapS = THREE.RepeatWrapping;
                        texture.repeat.x = -1;

                        this.imageCache[image360.file] = texture;
                        image360.texture = texture;

                        resolve(texture);
                    });

                    return texture;
                })
                .catch((error) => {
                    console.warn(error);
                    reject(error);
                });
        });
    }

    cancelActiveMeasuring() {
        if (
            this.viewer &&
            this.viewer.measuringTool &&
            this.viewer.measuringTool.cancel &&
            typeof this.viewer.measuringTool.cancel.callback === 'function'
        ) {
            this.viewer.measuringTool.cancel.callback();
        }
    }

    resetToOriginalMesh(image360) {
        if (this.isFocusingImage) return;
        if (!image360 || !image360.mesh || !image360.originalMesh) return;

        image360.mesh.position.copy(image360.originalMesh.position);
        image360.mesh.rotation.copy(image360.originalMesh.rotation);
        image360.mesh.scale.copy(image360.originalMesh.scale);

        image360.mesh.geometry = this.sg;
        image360.mesh.material = this.sm.clone();

        image360.mesh.visible = false;
        image360.mesh.material.needsUpdate = true;
    }

    unfocus(usedViewButtons = false) {
        this.viewer.orbitControls.invertYaw = false;
        this.viewer.orbitControls.invertPitch = false;
        this.viewer.orbitControls.rotationSpeed = 5;
        if (this.viewer.hidePointcloudsInPanorama) {
            Utils.handlePointCloudTransparencySliderChange(0, this.viewer.useHQ);
        }
        this.handleCancelCalibratedMeasurements();
        this.cancelActiveMeasuring();

        let image = this.focusedImage;
        this.focusByClick = false;
        if (image === null) {
            return;
        }

        window.dispatchEvent(new CustomEvent('panoramaUnfocused'));

        this.cancelPromises = true;
        setMaxNodesLoading(4);

        if (this.zoomTween && this.zoomTween.isPlaying) this.zoomTween.stop();
        if (this.focusedImage && typeof this.focusedImage.parentNode.originalRotation !== 'undefined') {
            this.focusedImage.parentNode.rotation.copy(this.focusedImage.parentNode.originalRotation);
            this.focusedImage.parentNode.originalRotation = undefined;
        }
        this.imageUnfocused = true;
        this.isFocusingImage = false;
        this.loadingIndicator.style.display = 'none';

        this.splitScreen = false;
        this.elSplitScreen.src = Potree.resourcePath + '/icons/split_screen.svg';

        if (this.viewer.annotationTool.callbacks.cancel) {
            this.viewer.annotationTool.callbacks.cancel();
        }
        this.selectingEnabled = true;
        for (let image of this.images) {
            this.resetToOriginalMesh(image);
            image.mesh.visible = true;
            image.mesh.material.opacity = sphereOpactiy;
            image.mesh.material.needsUpdate = true;
        }

        this.sphere.material.map = null;
        this.sphere.visible = false;
        this.sphere.material.needsUpdate = true;

        this.viewer.orbitControls.doubleClockZoomEnabled = true;
        this.viewer.orbitControls.removeEventListeners('mousewheel');
        this.viewer.orbitControls.addEventListener('mousewheel', this.viewer.orbitControls.scroll);
        this.viewer.setControls(this.viewer.previousView.controls);
        this.viewer.setMoveSpeed(this.viewer.previousView.moveSpeed);

        const imagePosition = new THREE.Vector3();
        image.mesh.getWorldPosition(imagePosition);

        const currentDirection = this.viewer.scene.view.direction.clone();
        const closeOffset = 1;

        let cameraPosition = imagePosition.clone().add(currentDirection.multiplyScalar(-closeOffset));
        cameraPosition.z = imagePosition.z;
        const lookAtTarget = cameraPosition.clone();

        this.viewer.scene.view.setView(cameraPosition, lookAtTarget, 500);

        // Restore the original camera projection mode
        if (this.originalCameraMode !== undefined) {
            this.viewer.setCameraMode(this.originalCameraMode);
            this.originalCameraMode = undefined;
        }

        this.viewer.previousView = {
            controls: null,
            position: null,
            target: null,
            moveSpeed: null,
        };

        this.focusedImage = null;

        this.elUnfocus.style.display = 'none';
        this.elSplitScreen.style.display = 'none';
        this.elWallpaperOutlined.style.display = 'none';
        this.navigationArrowElement.style.display = 'none';

        this.showCropToolVolumes();

        this.dispatchEvent({
            type: 'image_unfocused',
            usedViewButtons,
        });
    }

    isChildNodeVisible(childnode, focusedImage) {
        if (focusedImage) {
            return childnode.showImage && childnode.image360 !== undefined && focusedImage.mesh.uuid !== childnode.uuid;
        } else if (childnode.visible === false || (childnode.visible === true && childnode.image360 === undefined)) {
            return false;
        } else {
            return true;
        }
    }

    updateNavigationArrowImage(direction, groupName) {
        if (this.navigationArrowTextureCache.has(direction)) {
            this.applyNavigationArrowTexture(this.navigationArrowTextureCache.get(direction));
        } else {
            this.textureLoader.load(this.navigationArrowImages[direction], (texture) => {
                texture.colorSpace = THREE.SRGBColorSpace;
                const image = texture.image;
                const canvas = document.createElement('canvas');
                canvas.width = THREE.MathUtils.ceilPowerOfTwo(image.width);
                canvas.height = THREE.MathUtils.ceilPowerOfTwo(image.height);
                const ctx = canvas.getContext('2d');
                ctx.drawImage(image, 0, 0, canvas.width, canvas.height);
                texture.image = canvas;

                texture.transparent = true;
                this.navigationArrowTextureCache.set(direction, texture);
                this.applyNavigationArrowTexture(texture);
            });
        }
    }

    applyNavigationArrowTexture(texture) {
        const material = new THREE.SpriteMaterial({
            map: texture,
            transparent: true,
            opacity: 0.7,
            sizeAttenuation: false,
        });

        if (!this.navigationArrow || (!this.navigationArrow.uuid && this.focusedImage && this.focusedImage.groupId)) {
            this.navigationArrow = new THREE.Sprite(material);
            this.node[this.focusedImage.groupId].add(this.navigationArrow);
        }

        if (this.navigationArrow && this.navigationArrow.uuid) {
            this.navigationArrow.material = material;
            this.navigationArrow.material.map = texture;
            const zoom = this.viewer && this.viewer.scene && this.viewer.scene.cameraP ? this.viewer.scene.cameraP.zoom : 1;
            this.navigationArrow.scale.set(0.1 / zoom, 0.1 / zoom, 0.1 / zoom);
            this.navigationArrow.material.map.needsUpdate = true;
        }
    }

    showNavigationArrow(currentlyHovered, intersection) {
        const currentlyHoveredPosition = new THREE.Vector3(
            currentlyHovered.position.x,
            currentlyHovered.position.y,
            currentlyHovered.position.z
        );
        const focusedImagePosition = new THREE.Vector3(
            this.focusedImage.position[0],
            this.focusedImage.position[1],
            this.focusedImage.position[2]
        );

        const sphereDirection = focusedImagePosition.clone().sub(currentlyHoveredPosition).normalize();

        const angleInRadians = Math.atan2(sphereDirection.y, sphereDirection.x);
        let angleInDegrees = THREE.MathUtils.radToDeg(angleInRadians);
        if (angleInDegrees < 0) {
            angleInDegrees += 360;
        }

        if (
            angleInDegrees >= 347.5 ||
            angleInDegrees < 22.5 ||
            (angleInDegrees >= 67.5 && angleInDegrees < 112.5) ||
            (angleInDegrees >= 157.5 && angleInDegrees < 202.5) ||
            (angleInDegrees >= 247.5 && angleInDegrees < 292.5)
        ) {
            this.updateNavigationArrowImage('Straight');
        } else if ((angleInDegrees >= 22.5 && angleInDegrees < 45) || (angleInDegrees >= 112.5 && angleInDegrees < 135)) {
            this.updateNavigationArrowImage('RightTilt22');
        } else if ((angleInDegrees >= 45 && angleInDegrees < 67.5) || (angleInDegrees >= 135 && angleInDegrees < 157.5)) {
            this.updateNavigationArrowImage('RightTilt45');
        } else if ((angleInDegrees >= 202.5 && angleInDegrees < 225) || (angleInDegrees >= 292.5 && angleInDegrees < 315)) {
            this.updateNavigationArrowImage('LeftTilt22');
        } else if ((angleInDegrees >= 222.5 && angleInDegrees < 247.5) || (angleInDegrees >= 315 && angleInDegrees < 347.5)) {
            this.updateNavigationArrowImage('LeftTilt45');
        }

        if (this.navigationArrow && this.navigationArrow.uuid && this.focusedImage) {
            const pos = new THREE.Vector3();
            const worldQuaternion = new THREE.Quaternion();

            this.node[this.focusedImage.groupId].getWorldPosition(pos);
            this.node[this.focusedImage.groupId].getWorldQuaternion(worldQuaternion);

            let result = intersection.point.clone().sub(pos);

            const inverseWorldQuaternion = worldQuaternion.invert();
            result.applyQuaternion(inverseWorldQuaternion);

            this.navigationArrow.position.copy(result);
            if (this.panoramaImageSetting === panoramaConfigurationValue.Arrow) {
                this.navigationArrow.visible = true;
            }
        }
    }

    handleHovering() {
        const mouse = this.viewer.inputHandler.mouse;
        const camera = this.viewer.scene.getActiveCamera();
        const domElement = this.viewer.renderer.domElement;

        this.removeNavigationArrowElement();

        const ray = Potree.Utils.mouseToRay(mouse, camera, domElement.clientWidth, domElement.clientHeight);
        this.raycaster.ray.copy(ray);

        const allChildren = Object.values(this.node).flatMap((group) => group.children);
        const visibleChildren = allChildren.filter((child) => this.isChildNodeVisible(child, this.focusedImage));
        const intersections = this.raycaster.intersectObjects(visibleChildren);
        this.navigationArrow.visible = false;

        if (this.zoomTween) {
            return;
        }

        const setting = this.panoramaImageSetting;
        const isNavigationMode = setting === panoramaConfigurationValue.Navigation;
        const isGroundCirclesMode = setting === panoramaConfigurationValue.GroundCircles;
        const isPointerMode = isNavigationMode || isGroundCirclesMode;

        const showPointerAtMouse = () => {
            const mouseX = mouse.x;
            const mouseY = mouse.y;

            this.navigationArrowElement = isGroundCirclesMode ? this.createGroundCricleMousePointer() : this.createNavigationArrowButton();

            this.domRoot.appendChild(this.navigationArrowElement);
            this.navigationArrowElement.style.left = `${mouseX - 15}px`;
            this.navigationArrowElement.style.top = `${mouseY - 15}px`;
            this.navigationArrowElement.style.display = 'block';
        };

        if (intersections.length === 0) {
            if (this.focusedImage && isPointerMode) {
                showPointerAtMouse();
            }
            return;
        }

        if (this.focusedImage && isNavigationMode) {
            showPointerAtMouse();
            return;
        }

        const intersection = intersections[0];
        this.currentlyHovered = intersection.object;

        if (this.focusedImage && setting === panoramaConfigurationValue.Arrow) {
            if (
                this.currentlyHovered &&
                this.focusedImage &&
                this.focusedImage.mesh &&
                this.focusedImage.mesh.uuid !== this.currentlyHovered.uuid
            ) {
                this.showNavigationArrow(this.currentlyHovered, intersection);
            } else {
                this.navigationArrow.visible = false;
                if (!this.focusedImage) {
                    this.currentlyHovered.material = this.smHovered;
                }
            }
        } else if (this.focusedImage && isGroundCirclesMode) {
            if (this.currentlyHovered.geometry === this.circleGeometry) {
                this.currentlyHovered.visible = true;
            }
            this.currentlyHovered.material = this.circleMaterialHovered;
            this.currentlyHovered.material.opacity = 0.2;
            this.currentlyHovered.material.needsUpdate = true;
        } else {
            this.currentlyHovered.visible = true;
            this.currentlyHovered.material = this.smHovered.clone();
            this.currentlyHovered.material.opacity = 0.2;
            this.currentlyHovered.material.needsUpdate = true;
        }
    }

    removeNavigationArrowElement() {
        if (this.navigationArrowElement && this.navigationArrowElement.parentElement) {
            this.navigationArrowElement.parentElement.removeChild(this.navigationArrowElement);
        }
        this.navigationArrowElement.style.display = 'none';
    }

    handleCancelCalibratedMeasurements() {
        this.panoramicFactor = null;
        const toDelete = this.viewer.scene.measurements.filter((m) => m.name === 'Panoramic_Measure');
        toDelete.forEach((m) => {
            this.viewer.scene.removeMeasurement(m);
        });
    }

    handleGroundCirclesSetting(image) {
        this.resetToOriginalMesh(image);
        const floorCircle = new THREE.Mesh(this.circleGeometry, this.circleMaterial.clone());
        floorCircle.rotation.x = -Math.PI;
        const sphereRadius = 1.6;
        floorCircle.position.set(
            image.originalMesh.position.x,
            image.originalMesh.position.y,
            image.originalMesh.position.z - sphereRadius
        );
        image.mesh.geometry = floorCircle.geometry;
        image.mesh.material = floorCircle.material;
        image.mesh.material.color.set(this.panoramaGroundCirclesColor);

        image.mesh.position.set(floorCircle.position.x, floorCircle.position.y, floorCircle.position.z);
        image.mesh.rotation.copy(floorCircle.rotation);

        image.mesh.material.opacity = sphereOpactiy;
        image.mesh.showImage = true;
        image.mesh.visible = true;
        image.mesh.material.needsUpdate = true;
    }

    handleArrowAndNavigationSetting(image) {
        this.resetToOriginalMesh(image);
        image.mesh.material.opacity = 0;
        image.mesh.scale.set(1.5, 5, 1);
        image.mesh.showImage = true;
        image.mesh.visible = false;
        image.mesh.material.needsUpdate = true;
    }

    handleBubbleSetting(image) {
        this.resetToOriginalMesh(image);
        image.mesh.material.opacity = sphereOpactiy;
        image.mesh.showImage = true;
        image.mesh.visible = true;
    }

    handleCurrentHover() {
        if (this.currentlyHovered) {
            if (this.focusedImage && this.panoramaImageSetting === panoramaConfigurationValue.GroundCircles) {
                this.currentlyHovered.material = this.circleMaterial;
                this.currentlyHovered.material.opacity = sphereOpactiy;
                this.currentlyHovered.material.color.set(this.panoramaGroundCirclesColor);

                if (this.currentlyHovered.geometry === this.circleGeometry) {
                    this.currentlyHovered.visible = true;
                }
            } else {
                this.currentlyHovered.material = this.sm.clone();
            }
            this.currentlyHovered.material.needsUpdate = true;
            this.currentlyHovered = null;
        }
    }

    update() {
        if (
            this.focusedImage &&
            this.lastPanoramaGroundCirclesColor !== this.panoramaGroundCirclesColor &&
            this.panoramaImageSetting === panoramaConfigurationValue.GroundCircles
        ) {
            this.lastPanoramaGroundCirclesColor = this.panoramaGroundCirclesColor;
            for (let image of this.images) {
                if (!image.mesh.showImage) continue;
                image.mesh.material.color.set(this.panoramaGroundCirclesColor);
            }
        }
        //Set camera mode to perspective when an image is focused
        if (this.focusedImage && this.viewer.scene.cameraMode !== cameraModePerspective) {
            this.originalCameraMode = this.viewer.scene.cameraMode;
            this.viewer.setCameraMode(cameraModePerspective);
        }
        // Update images only when the panorama setting changes
        if (this.focusedImage && this.lastPanoramaImageSetting !== this.panoramaImageSetting) {
            this.lastPanoramaImageSetting = this.panoramaImageSetting;
            for (let image of this.images) {
                if (!image.mesh.showImage) continue;
                switch (this.panoramaImageSetting) {
                    case panoramaConfigurationValue.GroundCircles:
                        this.handleGroundCirclesSetting(image);
                        break;
                    case panoramaConfigurationValue.Navigation:
                    case panoramaConfigurationValue.Arrow:
                        this.handleArrowAndNavigationSetting(image);
                        break;
                    case panoramaConfigurationValue.Bubble:
                        this.handleBubbleSetting(image);
                        break;
                    default:
                        console.warn(`Unknown panoramaImageSetting: ${this.panoramaImageSetting}`);
                }
            }
        }

        // Handle hover effects for the previously hovered image
        this.handleCurrentHover();

        // Check for new hovering interactions if enabled
        if (this.selectingEnabled && !this.hideNavigationArrow) {
            this.handleHovering();
        }
    }

    hideCropToolVolumes() {
        if (!this.viewer || !this.viewer.scene || !this.viewer.scene.volumes) {
            return;
        }

        for (let volume of this.viewer.scene.volumes) {
            if (volume.clip && volume.frame && !this.cropToolVolumesVisibilityState.has(volume.uuid)) {
                this.cropToolVolumesVisibilityState.set(volume.uuid, volume.frame.visible);
                volume.frame.visible = false;
            }
        }
    }

    showCropToolVolumes() {
        if (!this.viewer || !this.viewer.scene || !this.viewer.scene.volumes) {
            return;
        }

        for (let volume of this.viewer.scene.volumes) {
            if (volume.clip && volume.frame) {
                const originalVisibility = this.cropToolVolumesVisibilityState.get(volume.uuid);
                if (originalVisibility !== undefined) {
                    volume.frame.visible = originalVisibility;
                }
            }
        }

        this.cropToolVolumesVisibilityState.clear();
    }
}

export class Images360Loader {
    constructor() {
        this.loader = null;
        this.loaderArray = [];
        this.sg = new THREE.SphereGeometry(0.3, 16, 16);

        this.sm = new THREE.MeshStandardMaterial({
            side: THREE.BackSide,
            color: 0xffffff,
            transparent: true,
            opacity: sphereOpactiy,
            emissive: 0x88badd,
            roughness: 0.287,
            metalness: 0.385,
        });
    }

    async load(serverUrl, imagesPaths, groupId, viewer, params = {}, tenantId, potreeId, splitScreenViewer, signal) {
        if (!params.transform) {
            params.transform = {
                forward: (a) => a,
            };
        }

        if (!this.loader)
            this.loader = new Images360(viewer, params.facilitySphereRadius, params.groundCirclesColor, potreeId, splitScreenViewer);

        let loadedImages = [];
        for (let dir of imagesPaths) {
            if (signal?.aborted) return;

            let response = await fetch(dir.csvPath, { signal });
            let text = await response.text();
            let lines = text.split(/\r?\n/);
            let coordinateLines = lines.slice(1);
            for (let line of coordinateLines) {
                if (line.trim().length === 0) {
                    continue;
                }
                let tokens = line.split(';');
                let [id, filename, time, long, lat, alt, w, course, pitch, roll] = tokens.map((token) => token.trim());
                time = parseFloat(time);
                long = parseFloat(long);
                lat = parseFloat(lat);
                alt = parseFloat(alt);
                course = parseFloat(course);
                pitch = parseFloat(pitch);
                roll = parseFloat(roll);
                filename = filename.replace(/"/g, '');
                let filePath = dir.directoryName + filename;
                let thumbnailPath = filePath.replace('/webp/', '/thumbnails/').replace('.webp', '_thumb.webp');
                let encodedPath = encodeURIComponent(filePath);
                const DEFAULT_FACILITY_ID = 0;
                const file = `${serverUrl}/StorageApi/tenant/${tenantId}/facility/${DEFAULT_FACILITY_ID}/storage/signedurlbypath?relativePath=${encodedPath}`;
                let image360 = new Image360(file, time, long, lat, alt, course, pitch, roll, parseFloat(w), thumbnailPath);
                let xy = params.transform.forward([long, lat]);
                let position = [...xy, alt];
                image360.position = position;
                image360.groupId = groupId;

                this.loader.images.push(image360);
                loadedImages.push(image360);
            }
        }

        this.createSceneNodes(loadedImages, this.loader, params.transform, groupId);
        return this.loader;
    }

    createSceneNodes(images, images360, transform, groupId) {
        images360.createNode(groupId);

        for (let image360 of images) {
            let { longitude, latitude, altitude } = image360;
            let xy = transform.forward([longitude, latitude]);
            let mesh = new THREE.Mesh(this.sg, this.sm);
            mesh.userData.isPanoramaSphere = true;
            mesh.position.set(...xy, altitude);
            mesh.scale.set(1, 1, 1);
            mesh.material.transparent = true;
            mesh.material.opacity = 0.75;
            mesh.image360 = image360;

            {
                // orientation
                var { course, pitch, roll } = image360;
                mesh.rotation.set(
                    THREE.MathUtils.degToRad(+roll + 90),
                    THREE.MathUtils.degToRad(-pitch),
                    THREE.MathUtils.degToRad(-course + 90),
                    'ZYX'
                );
            }
            images360.node[groupId].add(mesh);

            image360.parentNode = images360.node[groupId];
            image360.mesh = mesh;
            image360.originalMesh = mesh.clone();
        }
        const boundingBox = new THREE.Box3();
        boundingBox.setFromObject(images360.node[groupId]);
        images360.node[groupId].boundingBox = boundingBox;
    }
}
